/**
 * The CONSUMER READER — turns a frozen contract read result into a Phase A
 * semantic observation.
 *
 * WHAT THIS LAYER OWNS
 *   The SERVER STATE of FE-1 Stage Work Item §7.5: "owned by the typed consumer
 *   layer; carries the contract's own status vocabulary and freshness, verbatim."
 *
 * WHAT THIS LAYER DOES NOT OWN
 *   It does NOT project a domain model. FE1-W0 Authorization Record §3.8 places
 *   "domain projection" in the Capability Definition Read Slice, which FE1-W0
 *   Execution Sequence v1.0 §4.1 maps to PHASE C. Phase B stops here, at the
 *   consumer interface. A domain projection written in Phase B would leave the
 *   opened surface IN SUBSTANCE even if every byte sat at an opened path
 *   (FE1-W0 Path Boundary v1.0 §7: "Location does not launder scope").
 *
 * IT CONSUMES THE PHASE A SEMANTIC SYSTEM AND ADDS NOTHING TO IT
 *   `frontend/src/shared/semantic/**` is the single authority for the Unknown / Empty /
 *   Loading / Error / Stale / Degraded / Unavailable / Non-Authority separations
 *   (FE-1 Stage Work Item §4.1 E). This module calls its constructors. It does
 *   NOT define a second `UnknownState`, a second authority value, a success
 *   predicate, or an elevation function.
 *
 * WHAT IT REFUSES TO TRANSLATE — the whole point of the layer
 *   AVAILABLE  is NOT translated into operational availability, readiness,
 *              assignment or commitment. It means snapshot data was retrieved.
 *   UNKNOWN    is NOT translated into empty, error, default or success.
 *   UNAVAILABLE is NOT translated into empty, unknown or success, and no raw
 *              implementation diagnostic is surfaced.
 *   PUBLISHED  is NOT translated into ready. DRAFT/DEACTIVATED are NOT translated
 *              into eligibility or execution permission.
 *   NON_AUTHORITY is carried as the fixed property it is; nothing here renders a
 *              read result as permission to act.
 *
 * DEPENDENCY DIRECTION
 *   The port is declared in `frontend/src/adapters/` and this module imports its
 *   TYPE only. That import is erased at build (`verbatimModuleSyntax`), and the
 *   port's own import of the contract mirror is likewise type-only, so the
 *   RUNTIME import graph is acyclic even though the two modules name each other.
 */

import {
  Freshness,
  FrontendCondition,
  observedAvailable,
  observedFrontendCondition,
  observedUnavailable,
  observedUnknown,
  type ReadModelObservation,
} from '../../../../shared/semantic/index';
import type { CapabilityDefinitionReadPort } from '../adapters';
import type {
  CapabilityDefinitionQueryV1,
  CapabilityDefinitionReadResultV1,
  CapabilityDefinitionSnapshotV1,
} from './contract';

/**
 * THE FRESHNESS THE FROZEN READ PLANE DOES NOT PUBLISH.
 *
 * `CapabilityDefinitionReadResultV1` carries a status, a capability id and a
 * snapshot. It carries NO freshness, and neither does the snapshot. The Phase A
 * observation type requires a freshness on every contract outcome, so this layer
 * must supply one, and there is exactly one honest choice:
 *
 *   `Freshness.UNKNOWN` — "an answer was obtained, and how current it is cannot
 *   be established."
 *
 * That is not fabricated data and not a default: it is the accurate statement
 * that the read plane published no currency for this read. `FRESH` would assert a
 * currency nothing established; `STALE` and `DEGRADED` would assert a diminution
 * nothing established. See the recorded finding `FE1-W0B-F-02`.
 *
 * This does NOT collapse the two UNKNOWNs. Presence UNKNOWN and freshness UNKNOWN
 * are branded apart in Phase A and stay apart here: an UNKNOWN result becomes
 * `observedUnknown(Freshness.UNKNOWN)`, whose `presence` and `freshness` are
 * different nominal types.
 */
export const UNPUBLISHED_READ_FRESHNESS = Freshness.UNKNOWN;

/**
 * Map a contract read result to a semantic observation.
 *
 * Total over the frozen status vocabulary. The three statuses map to the three
 * contract observations, and each keeps its own meaning:
 *
 *   AVAILABLE   -> available observation, carrying the snapshot
 *   UNKNOWN     -> unknown observation, carrying NO payload
 *   UNAVAILABLE -> unavailable observation, carrying NO payload and no diagnostic
 *
 * The `assertNever` at the end is unreachable while the mirror's union is exactly
 * the frozen three; adding a fourth status to the mirror is a compile error here.
 */
export function toCapabilityDefinitionObservation(
  result: CapabilityDefinitionReadResultV1,
): ReadModelObservation<CapabilityDefinitionSnapshotV1> {
  const freshness = UNPUBLISHED_READ_FRESHNESS;
  if (result.status === 'AVAILABLE') {
    return observedAvailable(result.snapshot, freshness);
  }
  if (result.status === 'UNKNOWN') {
    return observedUnknown(freshness);
  }
  if (result.status === 'UNAVAILABLE') {
    return observedUnavailable(freshness);
  }
  // Unreachable: the mirror's three variants are exhaustive. Kept as a value
  // rather than a `default` branch so a fourth status becomes a compile error.
  return unreachableStatus(result);
}

/**
 * The frontend defect raised when a port fails outside the read seam's own
 * reader handling.
 *
 * WHY THIS IS AN ERROR CONDITION AND NOT UNAVAILABLE
 *   The frozen seam maps a READER RuntimeException to UNAVAILABLE — that is a
 *   read-plane outcome, and the fixture adapter reproduces it inside its own
 *   `read`. A port that throws or rejects for any other reason (a wiring fault, a contract
 *   violation caught in code) is a FRONTEND ENGINEERING FAILURE, which Phase A
 *   models as `FrontendCondition.ERROR` and defines as never a contract outcome.
 *   Mapping it to UNAVAILABLE would fabricate a read-plane outcome for a frontend
 *   defect, which is the collapse FE-1 Stage Work Item §7.5 forbids.
 *
 *   The seam has no equivalent branch because the seam IS the boundary; this one
 *   exists because the frontend's boundary is split across a port.
 */
export class CapabilityDefinitionPortViolation extends Error {
  public constructor(cause: unknown) {
    super(
      'The capability definition read port failed outside the read seam. This is a ' +
        'frontend engineering failure (ERROR), not a read-model state.',
      { cause },
    );
    this.name = 'CapabilityDefinitionPortViolation';
  }
}

function unreachableStatus(result: never): never {
  throw new CapabilityDefinitionPortViolation(
    new Error(`unhandled capability definition read status: ${String(result)}`),
  );
}

/**
 * Read a capability definition through the port and observe it.
 *
 * This is the consumer layer's entry point. It reads, then maps — and it does not
 * do anything else with the result.
 */
export async function readCapabilityDefinition(
  port: CapabilityDefinitionReadPort,
  query: CapabilityDefinitionQueryV1,
): Promise<ReadModelObservation<CapabilityDefinitionSnapshotV1>> {
  let result: CapabilityDefinitionReadResultV1;
  try {
    result = await port.read(query);
  } catch {
    // A frontend engineering failure, reported as such and never absorbed into a
    // contract outcome. Deliberately NOT `observedUnavailable`: that would render
    // a wiring fault as "a functioning reader could not supply a trustworthy
    // response", which is a different and false statement.
    //
    // The caught value is deliberately NOT bound: the observation carries no
    // diagnostic slot, and surfacing one here would invent a diagnostic channel
    // the frozen read plane does not have.
    return observedFrontendCondition(FrontendCondition.ERROR);
  }
  return toCapabilityDefinitionObservation(result);
}
